'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { Button } from '@//components/Button';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTrackCarReservation } from '@//data/tracking/useLegacyTrackCarReservation';
import { useLegacyTrackEcomButtonClick } from '@//data/tracking/useLegacyTrackEcomButtonClick';
import { useLegacyTrackEcomModalInternalEvent } from '@//data/tracking/useLegacyTrackEcomModalInternalEvent';
import { useMarket } from '@//providers/MarketSettingsProvider';

const resolveMarketCode = (market: Market) => {
  switch (market) {
    case 'wayke':
      return 'SE';
    case 'drive':
      return 'NO';
    default:
      return 'SE';
  }
};

interface EcomButtonProps {
  vehicle: ElasticVehicle;
}

const EcomButton = ({ vehicle }: EcomButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const documentRef = useRef<HTMLDivElement | undefined>(undefined);
  const contextRef = useRef<any>(undefined);
  const market = useMarket();
  const { trackLegacyCarReservation } = useLegacyTrackCarReservation();
  const { trackLegacyEcomModalInternalEvent } = useLegacyTrackEcomModalInternalEvent();
  const { trackLegacyEcomButtonClick } = useLegacyTrackEcomButtonClick();

  useEffect(() => {
    // Only create DOM element
    if (!documentRef.current) {
      const element = document.createElement('div');
      element.id = 'ecom-web';
      documentRef.current = element;
      document.body.appendChild(element);
    }

    return () => {
      contextRef.current?.close();
      contextRef.current?.destroy();
      contextRef.current = undefined;

      if (documentRef.current && document.body.contains(documentRef.current)) {
        try {
          document.body.removeChild(documentRef.current);
        } catch (error) {
          console.warn('[EcomButton] Failed to remove ecom-web element:', error);
        }
        documentRef.current = undefined;
      }
    };
  }, []);

  const onClick = useCallback(async () => {
    if (!contextRef.current) {
      setIsLoading(true);
      trackLegacyEcomButtonClick(vehicle);
      try {
        const [{ default: WaykeEcomWeb }] = await Promise.all([
          import('@wayke-se/ecom-web'),
          import('@wayke-se/ecom-web/dist/index.css'),
        ]);

        contextRef.current = new WaykeEcomWeb({
          id: vehicle.id,
          rootId: 'ecom-web',
          ecomSdkConfig: {
            api: {
              address: process.env.NEXT_PUBLIC_ECOM_URL!,
            },
          },
          marketCode: resolveMarketCode(market),
          onEvent: (view, userEvent, currentStep, data) => {
            const information = {
              view,
              userEvent,
              currentStep,
              ...(data || {}),
            };
            trackLegacyEcomModalInternalEvent(vehicle, information);

            if (userEvent === 'CONFIRMATION_CREATE_ORDER_SUCCEEDED') {
              trackLegacyCarReservation(vehicle, information);
            }
          },
        });
      } finally {
        setIsLoading(false);
      }
    }

    contextRef.current?.start();
  }, [market, vehicle.id, trackLegacyEcomButtonClick]);

  return (
    <Button
      sizeLtMd="lg"
      sizeMd="xl"
      fullWidth
      variant="secondary"
      disabled={vehicle.commerce?.reserved}
      loading={isLoading}
      onClick={onClick}
    >
      Köp bil
    </Button>
  );
};

export default EcomButton;
